import type { MixerConfig } from '../config/minigames.config';
import { AttemptGrade, MiniGameKind, gradeOf, gradedShare, type MiniGame, type MiniGameAttempt } from './MiniGame';

export interface MixerStop {
  readonly ingredient: string;
  /** Needle angle where it stopped (degrees), null when the time ran out. */
  readonly angle: number | null;
  readonly grade: AttemptGrade;
}

/**
 * Concrete mixer (user 2026-10-04: "auch schwer, hundert Prozent zu schaffen"): per ingredient (water, cement,
 * gravel) a needle sweeps the dial at constant speed and a tap stops it – in the green zone it counts, on the line in
 * its middle in full. The zone sits somewhere else every time and narrows, the needle gets faster.
 */
export class MixerGame implements MiniGame {
  readonly kind = MiniGameKind.Mixer;
  readonly attemptsTotal: number;
  readonly stops: MixerStop[] = [];
  /** Needle position along its back-and-forth: 0 … 2 (0 → 1 left to right, 1 → 2 back). */
  private travel: number;
  private zone: number;
  /** Seconds since the last stop (pouring), null while the needle runs. */
  private pouring: number | null = null;
  private attemptTime = 0;
  private shareNow = 0;
  private lastGrade: AttemptGrade | null = null;
  private sinceGrade = Infinity;
  /** A tap since the last update: the needle stops where it was. */
  private pendingStop = false;

  constructor(
    private readonly config: MixerConfig,
    private readonly round: number,
    private readonly random: () => number,
  ) {
    this.attemptsTotal = config.ingredients.length;
    this.travel = random() * 2;
    this.zone = this.placeZone();
  }

  /** Needle angle (degrees, 0 = top, −dial/2 … +dial/2); frozen while pouring. */
  get needle(): number {
    const half = this.config.dialDegrees / 2;
    const t = this.travel <= 1 ? this.travel : 2 - this.travel;
    return -half + t * this.config.dialDegrees;
  }

  /** Centre of the current green zone (degrees). */
  get zoneCenter(): number {
    return this.zone;
  }

  get zoneHalfWidth(): number {
    const widths = this.config.zoneHalfWidths;
    return widths[Math.min(this.stops.length - (this.pouring !== null ? 1 : 0), widths.length - 1)] ?? widths[widths.length - 1]!;
  }

  /** Ingredient being dosed now (the last one while it pours). */
  get ingredient(): string {
    const i = Math.min(this.stops.length - (this.pouring !== null ? 1 : 0), this.config.ingredients.length - 1);
    return this.config.ingredients[Math.max(0, i)]!;
  }

  /** 0 … 1 of the pour after a stop, null while the needle runs. */
  get pour(): number | null {
    return this.pouring === null ? null : Math.min(1, this.pouring / this.config.pourSeconds);
  }

  get share(): number {
    return this.shareNow;
  }

  get grade(): AttemptGrade | null {
    return this.lastGrade;
  }

  get secondsSinceGrade(): number {
    return this.sinceGrade;
  }

  get attemptsMade(): number {
    return this.stops.length;
  }

  get finished(): boolean {
    return this.stops.length >= this.attemptsTotal && this.pouring === null;
  }

  get timeShare(): number {
    return this.pouring !== null ? 1 : Math.max(0, 1 - this.attemptTime / this.config.secondsPerStop);
  }

  tap(): void {
    if (this.finished || this.pouring !== null) return;
    this.pendingStop = true;
  }

  update(dt: number): MiniGameAttempt | null {
    this.sinceGrade += dt;
    if (this.finished) return null;
    if (this.pouring !== null) {
      this.pouring += dt;
      if (this.pouring >= this.config.pourSeconds) {
        this.pouring = null;
        this.attemptTime = 0;
        if (this.stops.length < this.attemptsTotal) this.zone = this.placeZone();
      }
      return null;
    }
    // a tap between frames stops the needle where it was at the tap, before it moves on
    if (this.pendingStop) {
      this.pendingStop = false;
      return this.judge(this.needle);
    }
    this.travel = (this.travel + dt / this.sweepSeconds()) % 2;
    this.attemptTime += dt;
    if (this.attemptTime >= this.config.secondsPerStop) return this.judge(null);
    return null;
  }

  /** One sweep over the dial (s): the round's, faster with every stop. */
  sweepSeconds(): number {
    const c = this.config;
    return c.sweepSeconds[Math.min(this.round, c.sweepSeconds.length - 1)]! / c.speedUpPerStop ** this.stops.length;
  }

  private placeZone(): number {
    const half = this.config.dialDegrees / 2;
    const width = this.config.zoneHalfWidths[Math.min(this.stops.length, this.config.zoneHalfWidths.length - 1)]!;
    const room = half - width - 8; // never glued to the end stops
    return (this.random() * 2 - 1) * room;
  }

  private judge(angle: number | null): MiniGameAttempt {
    const c = this.config;
    const zone = this.zoneHalfWidth;
    const distance = angle === null ? Infinity : Math.abs(angle - this.zone);
    const grade = gradeOf(distance, c.perfectHalfWidth, zone);
    const gained = gradedShare(distance, c.perfectHalfWidth, zone, c.inZone.near, c.inZone.edge) / this.attemptsTotal;
    this.shareNow = Math.min(1, this.shareNow + gained);
    this.stops.push({ ingredient: this.ingredient, angle, grade });
    this.lastGrade = grade;
    this.sinceGrade = 0;
    this.pouring = 0;
    return { grade, gained };
  }
}
